import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import {
  Weight, TrendingUp, MapPin, BarChart3, Package, Shield,
  Building2, Scale, AlertTriangle, CheckCircle2, Layers
} from "lucide-react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));
const fmtKg = (n) => n >= 1000 ? `${(n / 1000).toFixed(2)} t` : `${(n || 0).toFixed(2)} kg`;
const COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16"];

function KpiCard({ icon: Icon, label, value, sub, color }) {
  const cls = {
    sky: "bg-sky-50 border-sky-200 text-sky-600",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-600",
    amber: "bg-amber-50 border-amber-200 text-amber-600",
    red: "bg-red-50 border-red-200 text-red-600",
    violet: "bg-violet-50 border-violet-200 text-violet-600",
  };
  const c = cls[color] || cls.sky;
  return (
    <div data-testid={`tonnage-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-xl border p-4 ${c}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 opacity-70" />
        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-[11px] opacity-60 mt-1">{sub}</p>}
    </div>
  );
}

function CIMap({ mapPoints }) {
  if (!mapPoints || mapPoints.length === 0) {
    return (
      <div data-testid="ci-map" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-sky-500" /> Carte de Cote d'Ivoire - Repartition du tonnage
        </h3>
        <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">Aucune donnee geographique</div>
      </div>
    );
  }

  const communes = {
    "Cocody": { x: 355, y: 380 }, "Plateau": { x: 340, y: 388 }, "Yopougon": { x: 315, y: 385 },
    "Abobo": { x: 345, y: 365 }, "Marcory": { x: 345, y: 393 }, "Treichville": { x: 340, y: 396 },
    "Adjame": { x: 335, y: 373 }, "Koumassi": { x: 350, y: 398 }, "Port-Bouet": { x: 350, y: 408 },
    "Attiecoube": { x: 325, y: 378 }, "Anyama": { x: 343, y: 350 }, "Bingerville": { x: 370, y: 388 },
    "Grand-Bassam": { x: 390, y: 398 }, "Bouake": { x: 248, y: 218 }, "Daloa": { x: 158, y: 258 },
    "San-Pedro": { x: 158, y: 388 }, "Yamoussoukro": { x: 228, y: 268 }, "Korhogo": { x: 228, y: 78 },
    "Man": { x: 108, y: 208 }, "Gagnoa": { x: 178, y: 308 }, "Abengourou": { x: 338, y: 258 },
    "Divo": { x: 208, y: 338 }, "Soubre": { x: 148, y: 338 }, "Odienne": { x: 118, y: 88 },
    "Bondoukou": { x: 368, y: 198 }, "Ferkessedougou": { x: 268, y: 98 },
    "Sassandra": { x: 138, y: 398 }, "Aboisso": { x: 388, y: 348 },
  };

  const maxWeight = Math.max(...mapPoints.map(p => p.total_weight_kg || 1), 1);

  return (
    <div data-testid="ci-map" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-sky-500" /> Carte de Cote d'Ivoire - Repartition du tonnage
      </h3>
      <div className="relative w-full" style={{ paddingBottom: "85%" }}>
        <svg viewBox="0 0 450 450" className="absolute inset-0 w-full h-full">
          {/* CI outline - improved shape */}
          <path
            d="M115,25 L155,18 L195,22 L230,35 L265,22 L300,28 L335,42 L365,55 L390,70
               L405,100 L415,140 L410,180 L405,215 L400,250 L395,280 L398,310 L400,340
               L398,365 L390,388 L375,405 L355,418 L330,425 L300,428 L270,422
               L240,418 L210,425 L180,420 L150,410 L125,398 L105,378
               L85,355 L70,325 L58,290 L48,255 L42,220 L45,185
               L52,150 L60,118 L72,88 L88,58 L115,25Z"
            fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1.5"
          />
          {/* Country label */}
          <text x="225" y="175" textAnchor="middle" fill="#94a3b8" fontSize="11" fontWeight="500" letterSpacing="3">COTE D'IVOIRE</text>

          {/* Abidjan region highlight */}
          <ellipse cx="350" cy="388" rx="55" ry="35" fill="#e0f2fe" fillOpacity="0.5" stroke="#bae6fd" strokeWidth="0.5" strokeDasharray="3,3" />
          <text x="350" y="425" textAnchor="middle" fill="#0284c7" fontSize="8" fontWeight="600">ABIDJAN</text>

          {/* Map points */}
          {mapPoints.map((point, i) => {
            const pos = communes[point.commune];
            if (!pos) return null;
            const radius = Math.max(6, Math.min(22, (point.total_weight_kg / maxWeight) * 22 + 6));
            const opacity = 0.4 + (point.total_weight_kg / maxWeight) * 0.5;
            return (
              <g key={point.commune}>
                <circle cx={pos.x} cy={pos.y} r={radius + 5} fill={COLORS[i % COLORS.length]} opacity={opacity * 0.2} />
                <circle cx={pos.x} cy={pos.y} r={radius} fill={COLORS[i % COLORS.length]} opacity={opacity} stroke="white" strokeWidth="1.5" />
                <text x={pos.x} y={pos.y - radius - 4} textAnchor="middle" fill="#334155" fontSize="9" fontWeight="600">
                  {point.commune}
                </text>
                <text x={pos.x} y={pos.y + 3} textAnchor="middle" fill="#1e293b" fontSize="8" fontWeight="bold">
                  {point.total_weight_kg >= 1000 ? `${(point.total_weight_kg / 1000).toFixed(1)}t` : `${point.total_weight_kg.toFixed(0)}kg`}
                </text>
              </g>
            );
          })}

          <text x="30" y="445" fill="#94a3b8" fontSize="8">Taille = tonnage relatif</text>
        </svg>
      </div>
    </div>
  );
}

export default function TonnageAnalyticsPage() {
  const [data, setData] = useState(null);
  const [zoneData, setZoneData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [analytics, zones] = await Promise.all([
        axios.get(`${API}/destructions/tonnage-analytics`, { headers: getHeaders() }),
        axios.get(`${API}/destructions/tonnage-by-zone`, { headers: getHeaders() }),
      ]);
      setData(analytics.data);
      setZoneData(zones.data);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const summary = data?.summary || {};
  const categories = (data?.category_breakdown || []).filter(c => c.weight_kg > 0);
  const trend = data?.monthly_trend || [];
  const zones = zoneData?.zones || [];
  const mapPoints = zoneData?.map_points || [];
  const anonComp = zoneData?.anonymous_comparison || [];

  const estVsAtt = [
    { name: "Estime", value: summary.calculated_total_kg || 0 },
    { name: "Atteste", value: summary.attestation_total_kg || 0 },
  ];

  return (
    <Layout>
      <div data-testid="tonnage-analytics-page" className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/15 flex items-center justify-center">
              <Scale className="w-5 h-5 text-sky-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Analytics Tonnage</h1>
              <p className="text-xs text-slate-500">CMES INTEL-STOCK - Suivi du poids des produits, destructions et attestations</p>
            </div>
          </div>
          <a href="/hub" className="text-xs text-slate-500 hover:text-sky-600 transition-colors border border-slate-200 px-3 py-1.5 rounded-lg">Retour Hub</a>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard icon={Weight} label="Poids total" value={fmtKg(summary.total_weight_kg || 0)} sub="Tous produits" color="sky" />
          <KpiCard icon={Package} label="Stock actif" value={fmtKg(summary.active_weight_kg || 0)} sub="Produits en stock" color="emerald" />
          <KpiCard icon={AlertTriangle} label="Perimes" value={fmtKg(summary.expired_weight_kg || 0)} sub="A detruire" color="amber" />
          <KpiCard icon={CheckCircle2} label="Detruits" value={fmtKg(summary.destroyed_weight_kg || 0)} sub={`Atteste: ${fmtKg(summary.attestation_total_kg || 0)}`} color="red" />
          <KpiCard icon={BarChart3} label="Cout/kg" value={summary.cost_per_kg > 0 ? `${fmt(summary.cost_per_kg)} FCFA` : "N/A"} sub="Cout destruction" color="violet" />
        </div>

        {/* Row: Trend + Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Monthly trend */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-sky-500" /> Tendance mensuelle (destructions)
            </h3>
            {trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}t` : `${v}kg`} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#334155" }} formatter={v => fmtKg(v)} />
                  <Area type="monotone" dataKey="weight_kg" stroke="#0ea5e9" fill="url(#trendGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-slate-400 text-sm">Pas encore de donnees</div>
            )}
          </div>

          {/* Category breakdown */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4 text-amber-500" /> Repartition par categorie
            </h3>
            {categories.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={categories} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="weight_kg" nameKey="category">
                      {categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#334155" }} formatter={v => fmtKg(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-3 mt-1">
                  {categories.slice(0, 6).map((c, i) => (
                    <div key={c.category} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      {c.category} ({fmtKg(c.weight_kg)})
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-slate-400 text-sm">Ajoutez le poids unitaire a vos produits</div>
            )}
          </div>
        </div>

        {/* Row: Estimated vs Attested + Anonymous comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Scale className="w-4 h-4 text-emerald-500" /> Poids estime vs atteste
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={estVsAtt} barGap={16}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={v => fmtKg(v)} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#334155" }} formatter={v => fmtKg(v)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  <Cell fill="#3b82f6" />
                  <Cell fill="#10b981" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-600"><div className="w-3 h-3 bg-blue-500 rounded" /> Estime (calcule)</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-600"><div className="w-3 h-3 bg-emerald-500 rounded" /> Atteste (autorites)</div>
            </div>
          </div>

          {/* Anonymous pharmacy comparison */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-violet-500" /> Comparaison anonyme (votre zone)
            </h3>
            {anonComp.length >= 3 ? (
              <div className="space-y-2">
                {anonComp.map((p) => (
                  <div key={p.rank} className={`flex items-center gap-3 p-2.5 rounded-lg ${p.is_me ? "bg-sky-50 border border-sky-200" : "bg-slate-50 border border-slate-100"}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${p.rank <= 3 ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"}`}>
                      {p.rank}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">
                        {p.is_me ? "Vous" : `Pharmacie #${p.rank}`}
                        {p.is_me && <span className="ml-2 text-[10px] text-sky-600 font-semibold bg-sky-100 px-1.5 py-0.5 rounded">VOUS</span>}
                      </p>
                      <p className="text-[10px] text-slate-400">{p.product_count} produits</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-800">{fmtKg(p.total_weight_kg)}</p>
                      <p className="text-[10px] text-slate-400">Detruit: {fmtKg(p.destroyed_weight_kg)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-slate-400 text-sm">
                <Shield className="w-8 h-8 mb-2 text-slate-300" />
                <p>Min 3 pharmacies requises dans votre zone</p>
                <p className="text-xs text-slate-400 mt-1">pour garantir l'anonymat</p>
              </div>
            )}
          </div>
        </div>

        {/* Map + Zone Table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CIMap mapPoints={mapPoints} />

          {/* Zone breakdown table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-sky-500" /> Repartition par zone
            </h3>
            {zones.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2 px-2">Commune</th>
                      <th className="text-right py-2 px-2">Pharma</th>
                      <th className="text-right py-2 px-2">Poids total</th>
                      <th className="text-right py-2 px-2">Detruit</th>
                      <th className="text-right py-2 px-2">Atteste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((z, i) => (
                      <tr key={z.commune} className={`border-b border-slate-100 ${z.commune === zoneData?.my_commune ? "bg-sky-50" : ""} hover:bg-slate-50`}>
                        <td className="py-2.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                            <span className="text-slate-700 font-medium">{z.commune}</span>
                            {z.commune === zoneData?.my_commune && <span className="text-[9px] text-sky-600 bg-sky-100 px-1 rounded">MA ZONE</span>}
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-right text-slate-500">{z.pharmacies}</td>
                        <td className="py-2.5 px-2 text-right text-slate-800 font-medium">{fmtKg(z.total_weight_kg)}</td>
                        <td className="py-2.5 px-2 text-right text-red-600">{fmtKg(z.destroyed_weight_kg)}</td>
                        <td className="py-2.5 px-2 text-right text-emerald-600">{fmtKg(z.attestation_weight_kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">Aucune donnee de zone</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
